import { createSlice } from '@reduxjs/toolkit';

import { createSuccess as appointmentCreateSuccess } from 'FRONTEND/reducers/appointmentSlice';
import { goToGivenExpertForm, goToGivenSlotForm, goToMultipleCalendar, goToLocationForm, goToUnrestrictedForm, goToPatientForm } from 'FRONTEND/reducers/calendarSlice';
import { createOnePatient } from 'FRONTEND/reducers/patientSlice';
import {
  APPOINTMENT_DEFAULT_DURATION,
  BOOKABLE_APPOINTMENT_TYPE_LIST,
  VIDEO_CONSULTATION,
  VIDEO_SPECIALIST_ASSESSMENT,
  FormTypes,
} from 'FRONTEND/utils/constants';
import { getDeserializedState } from 'FRONTEND/utils/serialization';

const REDUCER_NAME = 'booking';

// Relative selector for usage in the reducer
const storedState = getDeserializedState()?.[REDUCER_NAME];
const initialState = {
  selectedSlot: storedState?.selectedSlot || null,
  selectedType: storedState?.selectedType || null,
  selectedPatientId: storedState?.selectedPatientId || null,
  selectedExpertId: storedState?.selectedExpertId || null,
  selectedLocationId: storedState?.selectedLocationId || null,
  additionalSelectedExpertIdList: [],
  availableTypeList: storedState?.availableTypeList || BOOKABLE_APPOINTMENT_TYPE_LIST,
  isThirdPartyNotifEnabled: storedState?.isThirdPartyNotifEnabled || false,
  thirdPartyEmailList: storedState?.thirdPartyEmailList || [],
  reason: storedState?.reason || null,
  isPatientNotifEnabled: false,
  formType: storedState?.formType || FormTypes.GIVEN_EXPERT_FORM,
  desiredDateTime: storedState?.desiredDateTime || null,
  desiredDuration: storedState?.desiredDuration || null,
  defaultDuration: APPOINTMENT_DEFAULT_DURATION,
  hasPayment: storedState?.hasPayment || null,
  desiredConsultationCode: storedState?.desiredConsultationCode || null,
  isPayLater: storedState?.isPayLater || false,
  shouldClearForm: false,
  isBooth: storedState?.isBooth || null,
};

const bookingSlice = createSlice({
  name: REDUCER_NAME,
  initialState,
  reducers: {
    updateAvailableTypeList(state, { payload: { availableTypeList } }) {
      state.availableTypeList = availableTypeList;
    },
    pickType(state, { payload: { type } }) {
      state.selectedType = type;
    },
    pickSlot(state, { payload: { slot } }) {
      state.selectedSlot = slot;
    },
    pickPatient(state, { payload: { id } }) {
      state.selectedPatientId = id;
    },
    pickLocation(state, { payload: { id } }) {
      state.selectedLocationId = id;
    },
    pickExpert(state, { payload: { id } }) {
      if (state.formType !== FormTypes.GIVEN_SLOT_FORM && state.formType !== FormTypes.LOCATION_FORM && state.formType !== FormTypes.PATIENT_FORM) {
        state.selectedType = null;
      }
      state.selectedExpertId = id;
    },
    pickAdditionalExpert(state, { payload: { id } }) {
      if (state.additionalSelectedExpertIdList.indexOf(id) < 0) {
        state.additionalSelectedExpertIdList.push(id);
      }
    },
    pickDesiredConsultationCode(state, { payload: { code } }) {
      state.desiredConsultationCode = code;
    },
    pickBooth(state, { payload: { isBooth } }) {
      state.isBooth = isBooth;
    },
    unpickAdditionalExpert(state, { payload }) {
      state.additionalSelectedExpertIdList = state.additionalSelectedExpertIdList.filter((id) => id !== payload.id);
    },
    resetAdditionalExpertList(state) {
      state.additionalSelectedExpertIdList = [];
    },
    pickDateTime(state, { payload: { desiredDateTime } }) {
      state.desiredDateTime = desiredDateTime;
    },
    pickDuration(state, { payload: { desiredDuration } }) {
      state.desiredDuration = desiredDuration;
    },
    updateReason(state, { payload: { reason } }) {
      state.reason = reason;
    },
    updateIsThirdPartyNotifEnabled(state, { payload: { isThirdPartyNotifEnabled } }) {
      state.isThirdPartyNotifEnabled = isThirdPartyNotifEnabled;
    },
    updateThirdPartyEmailList(state, { payload: { emailList } }) {
      state.thirdPartyEmailList = emailList;
    },
    updateIsPatientNotifEnabled(state, { payload: { isEnabled } }) {
      state.isPatientNotifEnabled = isEnabled;
    },
    updateHasPayment(state, { payload: { hasPayment } }) {
      state.hasPayment = hasPayment;
    },
    updatePayLater(state, { payload: { isPayLater } }) {
      state.isPayLater = isPayLater;
    },
    clearForm(state) { // This forces a form reset while the form is already open
      state.shouldClearForm = true;
    },
    formCleared(state) {
      state.shouldClearForm = false;
    },
    resetBookingForm(state) {
      state.selectedSlot = null;
      state.selectedType = null;
      state.selectedPatientId = null;
      state.selectedExpertId = null;
      state.selectedLocationId = null;
      state.isThirdPartyNotifEnabled = false;
      state.thirdPartyEmailList = [];
      state.reason = null;
      state.isPatientNotifEnabled = false;
      state.additionalSelectedExpertIdList = [];
      state.desiredDateTime = null;
      state.isBooth = null;
      state.hasPayment = null;
      state.desiredConsultationCode = null;
      state.isPayLater = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(goToGivenSlotForm, (state) => {
      state.selectedExpertId = null;
      state.additionalSelectedExpertIdList = [];
      if (state.formType !== FormTypes.GIVEN_SLOT_FORM) {
        state.formType = FormTypes.GIVEN_SLOT_FORM;
      }
    });
    builder.addCase(goToGivenExpertForm, (state) => {
      if (state.formType !== FormTypes.GIVEN_EXPERT_FORM) {
        state.formType = FormTypes.GIVEN_EXPERT_FORM;
      }
    });
    builder.addCase(goToLocationForm, (state) => {
      state.selectedExpertId = null;
      state.additionalSelectedExpertIdList = [];
      state.selectedType = VIDEO_CONSULTATION;
      if (state.formType !== FormTypes.LOCATION_FORM) {
        state.formType = FormTypes.LOCATION_FORM;
      }
    });
    builder.addCase(goToUnrestrictedForm, (state) => {
      state.selectedExpertId = null;
      state.additionalSelectedExpertIdList = [];
      state.selectedType = VIDEO_SPECIALIST_ASSESSMENT;
      state.formType = FormTypes.UNRESTRICTED_FORM;
    });
    builder.addCase(goToPatientForm, (state) => {
      state.selectedExpertId = null;
      state.additionalSelectedExpertIdList = [];
      state.selectedType = VIDEO_CONSULTATION;
      state.formType = FormTypes.PATIENT_FORM;
    });
    builder.addCase(goToMultipleCalendar, (state) => {
      state.selectedExpertId = null;
      state.additionalSelectedExpertIdList = [];
    });
    builder.addCase(createOnePatient.fulfilled, (state, { payload: { id } }) => {
      if (state.isExpectingCreation) {
        state.selectedPatientId = id;
        state.isExpectingCreation = false;
      }
    });
    builder.addCase(appointmentCreateSuccess, (state, { payload: { id } }) => {
      // Reset selectedType to null to trigger a form reset, except for FormTypes.LOCATION_FORM which always uses VIDEO_CONSULTATION
      state.selectedType = state.formType === FormTypes.LOCATION_FORM ? VIDEO_CONSULTATION : null;
      state.selectedLocationId = null;
      state.selectedExpertId = null;
    });
  },
});

export const {
  updateAvailableTypeList,
  pickType,
  pickSlot,
  updateReason,
  updateIsThirdPartyNotifEnabled,
  updateThirdPartyEmailList,
  updateIsPatientNotifEnabled,
  updateHasPayment,
  updatePayLater,
  pickDateTime,
  pickDuration,
  clearForm,
  formCleared,
  pickPatient,
  pickLocation,
  pickExpert,
  pickAdditionalExpert,
  pickDesiredConsultationCode,
  pickBooth,
  unpickAdditionalExpert,
  resetAdditionalExpertList,
  resetBookingForm,
} = bookingSlice.actions;
export default bookingSlice.reducer;
